import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Leaf, Sprout, Star, Stethoscope } from "lucide-react";
import { api, playState } from "../../lib/api";
import { PhoneFrame } from "../../components/game/PhoneFrame";
import { PauseNotice, ScaleInput, useScales } from "../../components/game/PainFatigue";
import { useI18n } from "../../i18n";
import { CelebrationPhoto } from "../../components/game/CelebrationPhoto";

export default function SessionEnd() {
  const nav = useNavigate();
  const { t } = useI18n();
  const { state = {} } = useLocation();
  const { sessionId } = playState.get();
  const sc = useScales(0, 0);
  const [result, setResult] = useState(null);
  const sending = useRef(false);

  useEffect(() => { if (!sessionId) nav("/patient"); }, [sessionId, nav]);

  const submit = async () => {
    if (sending.current) return;
    sending.current = true;
    const { data } = await api.post(`/sessions/${sessionId}/complete`, {
      pain_after: sc.pain, fatigue_after: sc.fatigue, stopped_early: !!state?.stopped,
      level_completed: !!state?.levelCompleted, stars: state?.stars || 0, leaves: state?.leaves || 0,
    });
    setResult(data);
  };

  const m = result?.report.metrics;
  return (
    <PhoneFrame className="overflow-y-auto">
      <div className="flex min-h-full flex-col gap-4 bg-gradient-to-b from-[#EAF7F5] to-[#E6F7F2] p-5 pb-10" style={{ touchAction: "pan-y" }}>
        {!result ? (
          <>
            <p className="pt-6 text-xs font-bold tracking-[0.3em] text-[#0F7C7C]">{state?.stopped ? "SÉANCE ARRÊTÉE" : "FIN DE SÉANCE"}</p>
            <h1 className="font-display text-3xl font-extrabold text-[#0A2540]">Comment te sens-tu après l'aventure ?</h1>
            <ScaleInput label={t("painNow")} value={sc.pain} onChange={sc.setPain} testId="pain-slider-after" />
            <ScaleInput label={t("fatigueNow")} value={sc.fatigue} onChange={sc.setFatigue} testId="fatigue-slider-after" />
            {sc.pain >= 7 && <PauseNotice text={t("pauseMsg")} />}
            <button data-testid="session-end-submit" onClick={submit} className="h-16 rounded-full bg-brand text-lg font-extrabold text-white">Voir mes résultats</button>
          </>
        ) : (
          <div data-testid="session-results" className="flex flex-col gap-4">
            <div className="relative overflow-hidden rounded-3xl">
              <img decoding="async" src={result.rewards.seeds ? "/assets/village_bg_revived.webp" : "/assets/village_bg.webp"} alt="Village de Bao" className="h-52 w-full object-cover object-top" />
              {result.rewards.seeds > 0 && (
                <div className="absolute inset-x-3 bottom-3 rounded-2xl bg-[#0A2540]/85 p-3 text-center text-white backdrop-blur" data-testid="results-seed">
                  <p className="font-display text-xl font-extrabold">{t("seedFound")}</p><p className="font-bold text-[#E0B33A]">{t("plusSeed")}</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              {[[Sprout, result.rewards.seeds, "Graine"], [Leaf, result.rewards.leaves, "Feuilles"], [Star, result.rewards.stars, "Étoiles"]].map(([I, v, l]) => (
                <div key={l} className="rounded-2xl bg-white p-3 shadow-sm"><I className="mx-auto h-6 w-6 text-[#17A2A2]" /><p className="font-display text-2xl font-extrabold">+{v}</p><p className="text-xs text-slate-500">{l}</p></div>
              ))}
            </div>
            {[...result.rewards.new_items, ...result.rewards.new_animals].length > 0 && (
              <p className="rounded-2xl bg-white p-3 text-sm" data-testid="results-discoveries">Nouvelles découvertes : {[...result.rewards.new_items, ...result.rewards.new_animals].join(", ")}</p>
            )}
            {result.rewards.unlocked.length > 0 && <p className="rounded-2xl bg-[#0A2540] p-3 text-sm font-semibold text-white">Nouvelle zone débloquée sur la carte : La Savane</p>}
            <div className="rounded-3xl bg-white p-4 shadow-sm">
              <p className="text-xs font-bold tracking-widest text-slate-400">{t("simulated")}</p>
              <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                <p>Répétitions <b className="block font-display text-xl">{m.repetitions}</b></p>
                <p>Précision <b className="block font-display text-xl">{m.accuracy_mean ?? "—"}%</b></p>
                <p>Côté atteint <b className="block font-display text-xl">{m.affected_side_use_pct}%</b></p>
                <p>Régularité <b className="block font-display text-xl">{m.completion_pct}%</b></p>
              </div>
              <p className="mt-3 text-sm text-slate-600">Bao te remercie pour ta régularité et tes gestes précis — pas besoin d'aller vite.</p>
            </div>
            <CelebrationPhoto seeds={result.game_progress?.seeds ?? result.rewards.seeds} fresh={result.rewards.seeds} stars={result.rewards.stars} title="Le Village" pid={playState.get().patientId} onAlbum={() => nav("/patient/album")} />
            {result.pause_recommended && <PauseNotice text={t("pauseMsg")} />}
            <button data-testid="return-to-care-button" onClick={() => nav(`/care/sessions/${sessionId}/report`)}
              className="flex h-16 items-center justify-center gap-2 rounded-full bg-[#0A2540] text-lg font-extrabold text-white"><Stethoscope className="h-5 w-5" />Retour à Baobab Care</button>
            <button data-testid="results-home-button" onClick={() => nav("/")} className="h-14 rounded-full bg-white text-base font-bold">Accueil</button>
          </div>
        )}
      </div>
    </PhoneFrame>
  );
}
